import { RaidDifficulty } from '../../../../../../shared/gameConfig/sharedTypes';
import {
  RaidEncounterAttemptsType,
  StatisticsDataType,
  StatisticsMythicPlusDataTypes,
  StatisticsRaidingDataTypes,
  StatisticsScope,
  TStatsPatronCheck
} from '../../../../../../shared/types/statisticsTypes';
import {
  AVAILABLE_STATISTICS_RAIDS,
  AVAILABLE_STATISTICS_SEASONS,
  DEFAULT_STATISTICS_RAID_SLUG,
  DEFAULT_STATISTICS_SEASON_SLUG,
  STATISTICS_MYTHIC_PLUS_DATA_TYPES,
  STATISTICS_RAIDING_DATA_TYPES
} from '../../../../shared/statisticsUtils';
import { TMythicPlusStatisticsPageFilters, TRaidingStatisticsPageFilters, TStatisticPageTypes } from '../types';

export const ALL_OPTIONS_VALUE = 'all';

// Filter keys that are shared between M+ and Raid stats and should persist when navigating between groups
export const SHARED_CROSS_GROUP_FILTER_KEYS: (keyof TMythicPlusStatisticsPageFilters &
  keyof TRaidingStatisticsPageFilters)[] = ['region', 'scope', 'seasonWeekStart', 'seasonWeekEnd'];

export const DEFAULT_CHARACTER_SEASON_SLUG = DEFAULT_STATISTICS_SEASON_SLUG;
export const DEFAULT_CHARACTER_RAID_SLUG = DEFAULT_STATISTICS_RAID_SLUG;

export const DEFAULT_RAIDING_DIFFICULTY_VALUE = RaidDifficulty.MYTHIC;

export const DEFAULT_METRICS_VISIBILITY: Record<StatisticsDataType, boolean> = {
  // mythic plus
  [StatisticsDataType.MythicPlusDungeonSuccessRates]: true,
  [StatisticsDataType.MythicPlusRunsOverTime]: true,
  [StatisticsDataType.MythicPlusSpecPopularity]: true,
  [StatisticsDataType.MythicPlusGroupComps]: true,
  [StatisticsDataType.MythicPlusHeatmaps]: true,
  [StatisticsDataType.MythicPlusAbortedRuns]: true,
  [StatisticsDataType.MythicPlusEncounterAttempts]: true,
  // Internal materialization consumed by Weekly Routes, not a public Statistics page.
  [StatisticsDataType.MythicPlusBossOrder]: false,

  // event insights
  [StatisticsDataType.EventInsightsGroupComps]: true,
  [StatisticsDataType.EventInsightsMaps]: true,
  [StatisticsDataType.EventInsightsPopularSpecs]: true,

  // raiding
  [StatisticsDataType.RaidEncounterAttempts]: true,
  [StatisticsDataType.RaidSpecPopularity]: true,
  [StatisticsDataType.RaidGroupComps]: true,

  // character
  [StatisticsDataType.TierTalents]: false,
  [StatisticsDataType.TierGear]: false,
  [StatisticsDataType.TierTrinkets]: false
};

export enum StatisticMetricsMenuItemsGroup {
  MythicPlus = 'mythic-plus',
  Raiding = 'raiding'
}

export enum StatisticsCharacterViewTypes {
  MythicPlus = 'mythic-plus',
  Raid = 'raid'
}

export const STATISTICS_GROUPS_TO_DATA_TYPES_MAP: Record<StatisticMetricsMenuItemsGroup, StatisticsDataType[]> = {
  [StatisticMetricsMenuItemsGroup.MythicPlus]: STATISTICS_MYTHIC_PLUS_DATA_TYPES,
  [StatisticMetricsMenuItemsGroup.Raiding]: STATISTICS_RAIDING_DATA_TYPES
};

export const DEFAULT_CHARACTER_VIEW_TYPE: StatisticsCharacterViewTypes = StatisticsCharacterViewTypes.MythicPlus;

export const DEFAULT_STATISTICS_DATA_TYPE: StatisticsMythicPlusDataTypes = StatisticsDataType.MythicPlusRunsOverTime;
export const DEFAULT_STATISTICS_SCOPE = StatisticsScope.ThisReset;

export const ALLOW_CUSTOM_MPLUS_KEYS_RANGE = true;

export const MAX_SPECS_COUNT_PER_M_PLUS_RUN = 5;

export enum StatisticChartEmbedParams {
  EmbedMode = 'embedMode',
  Embed = 'embed',
  IncludePlayerSpellIds = 'includePlayerSpellIds'
}

export enum StatisticChartCustomizationParams {
  BackgroundUrl = 'backgroundUrl',
  BackgroundDark = 'backgroundDark',
  BackgroundSize = 'backgroundSize',
  Chromargb = 'chromargb',
  ShowPatch = 'patchView',
  HideHeader = 'hideHeader',
  HideFooter = 'hideFooter',
  ShowTitle = 'showTitle',
  ChartHeight = 'chartHeight',
  HideYAxisLabels = 'hideYAxisLabels',
  HideXAxisLabels = 'hideXAxisLabels',
  HideXGridLines = 'hideXGridLines',
  HideYGridLines = 'hideYGridLines',
  UseDungeonBackground = 'useDungeonBackground',
  EventInsightsMapsType = 'eventInsightsMapsType',
  Role = 'role',
  Limit = 'limit',
  ChartMode = 'chartMode'
}

export const DEFAULT_IGNORED_QUERY_PARAMS_FOR_FILTERS = [
  ...Object.values(StatisticChartEmbedParams),
  ...Object.values(StatisticChartCustomizationParams)
];

export const STATISTIC_TYPE_TO_ROUTE_TYPE_MAP: Record<TStatisticPageTypes, string> = {
  [StatisticsDataType.MythicPlusRunsOverTime]: 'mythic-plus-runs',
  [StatisticsDataType.MythicPlusSpecPopularity]: 'mythic-plus-spec-popularity',
  [StatisticsDataType.MythicPlusDungeonSuccessRates]: 'mythic-plus-dungeon-times',
  [StatisticsDataType.MythicPlusGroupComps]: 'mythic-plus-group-compositions',
  [StatisticsDataType.MythicPlusHeatmaps]: 'mythic-plus-heatmaps',
  [StatisticsDataType.MythicPlusAbortedRuns]: 'mythic-plus-abandoned-runs',
  [StatisticsDataType.MythicPlusEncounterAttempts]: 'mythic-plus-encounter-attempts',
  [StatisticsDataType.MythicPlusBossOrder]: 'mythic-plus-boss-order',
  [StatisticsDataType.EventInsightsGroupComps]: 'event-insights-group-comps',
  [StatisticsDataType.EventInsightsMaps]: 'event-insights-maps',
  [StatisticsDataType.EventInsightsPopularSpecs]: 'event-insights-spec-popularity',
  [StatisticsDataType.RaidEncounterAttempts]: 'raid-encounter-attempts',
  [StatisticsDataType.RaidSpecPopularity]: 'raid-spec-popularity',
  [StatisticsDataType.RaidGroupComps]: 'raid-group-comps'
};

export enum GroupCompsModes {
  Popularity = 'popularity',
  SuccessRate = 'success-rate'
}

export enum EncounterAttemptsModes {
  Default = 'default',
  SuccessRate = 'success-rate'
}

export enum SpecPopularityModes {
  BySpec = 'by-spec',
  ByClass = 'by-class'
}

export const DEFAULT_SPEC_POPULARITY_MODE: SpecPopularityModes = SpecPopularityModes.BySpec;

export const ROUTE_TYPE_TO_STATISTIC_TYPE_MAP = Object.fromEntries(
  Object.entries(STATISTIC_TYPE_TO_ROUTE_TYPE_MAP).map(([type, route]) => [route, type])
) as Record<string, StatisticsDataType>;

// Mapping from M+ types to corresponding Raid types (for intelligent cross-group navigation)
export const MPLUS_TO_RAID_TYPE_MAP: Partial<Record<StatisticsMythicPlusDataTypes, StatisticsRaidingDataTypes>> = {
  [StatisticsDataType.MythicPlusSpecPopularity]: StatisticsDataType.RaidSpecPopularity,
  [StatisticsDataType.MythicPlusGroupComps]: StatisticsDataType.RaidGroupComps,
  [StatisticsDataType.MythicPlusEncounterAttempts]: StatisticsDataType.RaidEncounterAttempts
};

// Mapping from Raid types to corresponding M+ types (derived from MPLUS_TO_RAID_TYPE_MAP)
export const RAID_TO_MPLUS_TYPE_MAP = Object.fromEntries(
  Object.entries(MPLUS_TO_RAID_TYPE_MAP).map(([mplus, raid]) => [raid, mplus])
) as Partial<Record<StatisticsRaidingDataTypes, StatisticsMythicPlusDataTypes>>;

// Default fallbacks when no corresponding type exists in the other group
export const DEFAULT_RAID_TYPE = StatisticsDataType.RaidSpecPopularity;
export const DEFAULT_MPLUS_TYPE = StatisticsDataType.MythicPlusRunsOverTime;
export const DEFAULT_CHARACTER_TYPE = StatisticsDataType.TierTalents;

export enum StatisticDataView {
  Table = 'table',
  Chart = 'chart'
}

// Available metric types for specific character/guild/team stats
export const AVAILABLE_METRIC_TYPES_FOR_SPECIFIC_CHARACTER_STATS: (
  | StatisticsMythicPlusDataTypes
  | StatisticsRaidingDataTypes
)[] = [
  // Mythic+
  StatisticsDataType.MythicPlusRunsOverTime,
  StatisticsDataType.MythicPlusSpecPopularity,
  StatisticsDataType.MythicPlusDungeonSuccessRates,
  StatisticsDataType.MythicPlusGroupComps,
  StatisticsDataType.MythicPlusAbortedRuns,
  StatisticsDataType.MythicPlusEncounterAttempts,

  // Raids
  StatisticsDataType.RaidSpecPopularity,
  StatisticsDataType.RaidGroupComps,
  StatisticsDataType.RaidEncounterAttempts
] as const;

// Patron checks for specific character/guild/team stats
export const PATRON_CHECKS_FOR_SPECIFIC_CHARACTER_STATS: TStatsPatronCheck[] = [
  'mythic-level-select',
  'custom-key-range',
  'custom-week-range',
  'prev-expansion-seasons',
  'group-spec-select'
];

// Icons by feature type (shared between M+ and Raid where applicable)
const ICONS = {
  specPopularity: 'fa-chart-user',
  groupComps: 'fa-people-group',
  encounterAttempts: 'fa-skull'
} as const;

export const STATISTIC_METRICS_ICONS: Record<StatisticsDataType, string | null> = {
  // Mythic Plus
  [StatisticsDataType.MythicPlusSpecPopularity]: ICONS.specPopularity,
  [StatisticsDataType.MythicPlusRunsOverTime]: 'fa-chart-column',
  [StatisticsDataType.MythicPlusDungeonSuccessRates]: 'fa-stopwatch',
  [StatisticsDataType.MythicPlusGroupComps]: ICONS.groupComps,
  [StatisticsDataType.MythicPlusHeatmaps]: 'fa-fire-flame-curved',
  [StatisticsDataType.MythicPlusAbortedRuns]: 'fa-person-from-portal',
  [StatisticsDataType.MythicPlusEncounterAttempts]: ICONS.encounterAttempts,
  [StatisticsDataType.MythicPlusBossOrder]: null,
  // Raiding
  [StatisticsDataType.RaidSpecPopularity]: ICONS.specPopularity,
  [StatisticsDataType.RaidGroupComps]: ICONS.groupComps,
  [StatisticsDataType.RaidEncounterAttempts]: ICONS.encounterAttempts,
  // Event Insights (no icons)
  [StatisticsDataType.EventInsightsGroupComps]: null,
  [StatisticsDataType.EventInsightsMaps]: null,
  [StatisticsDataType.EventInsightsPopularSpecs]: null,

  // Character
  [StatisticsDataType.TierTalents]: null,
  [StatisticsDataType.TierGear]: null,
  [StatisticsDataType.TierTrinkets]: null
};

export const STATISTIC_GROUPS_METRIC_ICON_SRC_MAP: Record<StatisticMetricsMenuItemsGroup, string> = {
  [StatisticMetricsMenuItemsGroup.MythicPlus]: AVAILABLE_STATISTICS_SEASONS.find((s) => s.icon)?.icon || '',
  [StatisticMetricsMenuItemsGroup.Raiding]: AVAILABLE_STATISTICS_RAIDS.find((s) => s.icon)?.icon || ''
};

export const BETA_STATISTICS_GROUPS: Partial<Record<StatisticMetricsMenuItemsGroup, boolean>> = {
  [StatisticMetricsMenuItemsGroup.Raiding]: true
};

// Individual stat type BETA badges - only show for stats whose parent group is NOT already marked as BETA
// This prevents redundant BETA badges (e.g., if "Raid Stats" group is BETA, individual raid stats don't need badges)
export const BETA_STATISTICS_TYPES: Partial<Record<StatisticsDataType, boolean>> = {
  // M+ stats that are in beta (group is not beta, so these show individual badges)
  [StatisticsDataType.MythicPlusAbortedRuns]: true,
  [StatisticsDataType.MythicPlusEncounterAttempts]: true
  // Note: Raid stats don't need individual BETA badges since the entire Raiding group is marked as BETA
};

export enum RaidEncounterAttemptsFrontendType {
  All = ALL_OPTIONS_VALUE,
  Successes = RaidEncounterAttemptsType.Successes,
  Wipes = RaidEncounterAttemptsType.Wipes
}

export const CHARACTER_VIEW_TYPE_TO_ICON_SRC_PATH_MAP: Record<StatisticsCharacterViewTypes, string> = {
  [StatisticsCharacterViewTypes.MythicPlus]: '/images/wow/icons/large/inv_misc_herb_mana_thistle_leaf.jpg',
  [StatisticsCharacterViewTypes.Raid]: '/images/wow/icons/large/inv_misc_herb_mana_thistle_leaf.jpg'
};
